import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { api, API, getToken } from "@/lib/api";
import { printQrLabels } from "@/lib/printLabels";
import PageHeader from "@/components/PageHeader";
import { labelFor, IconFor, statusCls } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { QrCode, ScanLine, MapPin, PackageSearch, Boxes, AlertTriangle, X, Camera, Printer, Download, ExternalLink, FileDown, FileText } from "lucide-react";

// Statuses meaning the item has physically LEFT storage — a stale storage
// location on such items pollutes the physical inventory.
const GONE_STATUSES = new Set([
  "restituted", "destroyed", "donated", "auctioned", "handed_to_authorities", "shipped", "delivered",
]);

function parseItemId(text) {
  if (!text) return null;
  const s = String(text).trim();
  const q = s.match(/[?&]item=([^&\s]+)/);
  if (q) return decodeURIComponent(q[1]);
  const uuid = s.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
  if (uuid) return uuid[0];
  return s;
}

export default function Storage() {
  const { t, lang } = useLang();
  const { partner } = useAuth();
  const { scope, selectedId, selected } = useEstablishment();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [clearing, setClearing] = useState(null);

  const load = useCallback(async () => {
    try { setItems(await api(scope("/items"))); }
    catch (e) { toast.error(e.message); }
  }, [scope]);

  useEffect(() => { load(); }, [load, selectedId]);

  // Physical inventory = items still in storage, grouped by location.
  const groups = useMemo(() => {
    if (!items) return [];
    const stored = items.filter((i) => i.status === "in_storage");
    const map = new Map();
    for (const it of stored) {
      const loc = (it.storage_location || "").trim() || "__none__";
      if (!map.has(loc)) map.set(loc, []);
      map.get(loc).push(it);
    }
    const arr = Array.from(map.entries()).map(([loc, list]) => ({ loc, list }));
    arr.sort((a, b) => {
      if (a.loc === "__none__") return 1;
      if (b.loc === "__none__") return -1;
      return a.loc.localeCompare(b.loc, "fr", { sensitivity: "base" });
    });
    return arr;
  }, [items]);

  // Stale locations: item left storage but a location is still attached.
  const stale = useMemo(() => {
    if (!items) return [];
    return items.filter((i) => (i.storage_location || "").trim() && GONE_STATUSES.has(i.status));
  }, [items]);

  async function clearLocation(id) {
    setClearing(id);
    try {
      await api(`/items/${id}/storage-location`, { method: "PATCH", body: { storage_location: "" } });
      toast.success(t("stg.locFreed"));
      await load();
    } catch (e) { toast.error(e.message); } finally { setClearing(null); }
  }

  async function clearAllStale() {
    setClearing("__all__");
    try {
      await Promise.all(stale.map((i) => api(`/items/${i.id}/storage-location`, { method: "PATCH", body: { storage_location: "" } })));
      toast.success(t("stg.locsFreed").replace("{n}", stale.length));
      await load();
    } catch (e) { toast.error(e.message); } finally { setClearing(null); }
  }

  const storedCount = groups.reduce((n, g) => n + g.list.length, 0);
  const locCount = groups.filter((g) => g.loc !== "__none__").length;
  const allStored = groups.flatMap((g) => g.list);

  function printAll() {
    if (!printQrLabels(allStored, t("stg.allLabels"))) {
      toast.error(t("reg.popupBlocked"));
    }
  }
  function printGroup(g) {
    const title = g.loc === "__none__" ? t("stg.noLocation") : t("stg.shelf").replace("{loc}", g.loc);
    if (!printQrLabels(g.list, title)) {
      toast.error(t("reg.popupBlocked"));
    }
  }

  async function exportAudit() {
    try {
      const res = await fetch(`${API}${scope("/items/audit/movements.csv")}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findloop-audit-deplacements.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("stg.exportDone"));
    } catch { toast.error(t("stg.exportImpossible")); }
  }

  // Occupancy per location (physical load), busiest first.
  const occupancy = groups
    .map((g) => ({ name: g.loc === "__none__" ? t("stg.noLocation") : g.loc, count: g.list.length, none: g.loc === "__none__" }))
    .sort((a, b) => b.count - a.count);
  const occAvg = occupancy.length ? occupancy.reduce((n, o) => n + o.count, 0) / occupancy.length : 0;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        title={t("stg.title")}
        subtitle={t("stg.subtitle")}
        testid="storage-header"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl font-semibold" onClick={exportAudit} disabled={storedCount === 0 && !occupancy.some((o) => o.count)} data-testid="export-audit-btn">
              <FileDown size={18} className="mr-1.5" /> {t("stg.exportAudit")}
            </Button>
            <Button variant="outline" className="rounded-xl font-semibold" onClick={printAll} disabled={storedCount === 0} data-testid="print-all-btn">
              <Printer size={18} className="mr-1.5" /> {t("stg.printAll")}
            </Button>
            <Button className="rounded-xl font-semibold" onClick={() => setScanOpen(true)} data-testid="open-scanner-btn">
              <ScanLine size={18} className="mr-1.5" /> {t("stg.scanQr")}
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-testid="storage-kpis">
        <KpiCard icon={Boxes} tone="indigo" label={t("stg.kObjects")} value={items === null ? "…" : storedCount} />
        <KpiCard icon={MapPin} tone="teal" label={t("stg.kLocations")} value={items === null ? "…" : locCount} />
        <KpiCard icon={AlertTriangle} tone="amber" label={t("stg.kToClear")} value={items === null ? "…" : stale.length} />
      </div>

      {/* Self-service client QR */}
      {partner?.id && (() => {
        const estParam = selectedId && selectedId !== "all" ? selectedId : "";
        const qrUrl = `${API}/partners/${partner.id}/lookup-qr.png${estParam ? `?e=${estParam}` : ""}`;
        const posterUrl = `${API}/partners/${partner.id}/selfservice-poster.pdf?lang=${lang}${estParam ? `&e=${estParam}` : ""}`;
        const openUrl = `/objets/${partner.id}${estParam ? `?e=${estParam}` : ""}`;
        const dlName = `findloop-libre-service-${estParam || partner.id}.png`;
        return (
        <div className="mb-6 rounded-2xl border border-border bg-white card-shadow p-5 flex flex-col sm:flex-row items-center gap-4" data-testid="self-service-qr">
          <img src={qrUrl} alt={t("stg.qrAlt")} className="h-24 w-24 rounded-lg border border-border bg-white p-1" data-testid="self-service-qr-img" />
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <p className="font-display font-bold text-findo-navy flex items-center gap-1.5 justify-center sm:justify-start"><QrCode size={16} className="text-findo-indigo" /> {t("stg.qrTitle")}</p>
            <p className="text-sm text-slate-500 mt-0.5">{t("stg.qrHint")}{estParam ? ` · ${selected?.name || ""}` : ""}</p>
            <div className="mt-2 flex items-center gap-3 justify-center sm:justify-start flex-wrap">
              <a href={qrUrl} download={dlName} className="inline-flex items-center gap-1 text-xs font-semibold text-findo-indigo hover:underline" data-testid="self-service-qr-download"><Download size={13} /> {t("stg.qrDownload")}</a>
              <a href={posterUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-findo-indigo hover:underline" data-testid="self-service-poster"><FileText size={13} /> {t("stg.posterPdf")}</a>
              <a href={openUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:underline" data-testid="self-service-open"><ExternalLink size={13} /> {t("stg.openPage")}</a>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Stale location alert */}
      {stale.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="stale-location-alert">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 grid place-items-center rounded-xl bg-amber-100 text-amber-700 shrink-0"><AlertTriangle size={20} /></span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-amber-900">
                {t("stg.staleTitle").replace("{n}", stale.length)}
              </p>
              <p className="text-sm text-amber-800">{t("stg.staleHint")}</p>
            </div>
            <Button size="sm" variant="outline" onClick={clearAllStale} disabled={clearing === "__all__"}
              className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0" data-testid="clear-all-stale-btn">
              {t("stg.clearAll")}
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {stale.map((it) => {
              const Icon = IconFor(it.category);
              return (
                <div key={it.id} className="flex items-center gap-3 bg-white rounded-xl border border-amber-200 p-3" data-testid={`stale-item-${it.id}`}>
                  <span className="h-9 w-9 grid place-items-center rounded-lg bg-slate-100 text-slate-500 shrink-0"><Icon size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{it.description || labelFor(it.category)}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${statusCls(it.status)}`}>{t(`st.${it.status}`)}</span>
                      <span className="inline-flex items-center gap-1 text-findo-indigo font-semibold"><MapPin size={11} /> {it.storage_location}</span>
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => clearLocation(it.id)} disabled={clearing === it.id}
                    className="rounded-full shrink-0" data-testid={`clear-stale-${it.id}`}>
                    {t("stg.clear")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Occupancy overview */}
      {storedCount > 0 && locCount > 0 && (
        <div className="mb-6 bg-white rounded-2xl border border-border card-shadow p-5" data-testid="occupancy-stats">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><Boxes size={18} className="text-findo-indigo" /> {t("stg.occupancy")}</h2>
            <span className="text-xs text-slate-400">{t("stg.occAvg").replace("{n}", occAvg.toFixed(1))}</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">{t("stg.occHint")}</p>
          <ResponsiveContainer width="100%" height={Math.max(120, occupancy.length * 42)}>
            <BarChart data={occupancy} layout="vertical" margin={{ left: 10, right: 20, top: 4, bottom: 4 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: "#334155" }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} formatter={(v) => [t("stg.objectsN").replace("{n}", v), t("stg.objectsAxis")]} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                {occupancy.map((o, i) => (
                  <Cell key={i} fill={o.none ? "#94a3b8" : (o.count > occAvg && occupancy.length > 1 ? "#f59e0b" : "#4453B4")} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Shelf plan */}
      <h2 className="font-display font-bold text-slate-900 text-lg mb-3 flex items-center gap-2"><Boxes size={20} className="text-findo-indigo" /> {t("stg.shelfPlan")}</h2>
      {items === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : storedCount === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl" data-testid="storage-empty">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-indigo/10 text-findo-indigo"><PackageSearch size={32} /></div>
          <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{t("stg.emptyTitle")}</h3>
          <p className="mt-1 text-slate-500 text-sm">{t("stg.emptyDesc")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shelf-plan-grid">
          {groups.map((g) => (
            <div key={g.loc} className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid={`shelf-group-${g.loc === "__none__" ? "none" : g.loc.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b border-border ${g.loc === "__none__" ? "bg-slate-50" : "bg-findo-indigo/5"}`}>
                <span className="font-display font-bold text-findo-navy flex items-center gap-1.5">
                  <MapPin size={15} className={g.loc === "__none__" ? "text-slate-400" : "text-findo-indigo"} />
                  {g.loc === "__none__" ? t("stg.noLocation") : g.loc}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => printGroup(g)} title={t("stg.printGroupTitle")}
                    data-testid={`print-group-${g.loc === "__none__" ? "none" : g.loc.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    className="h-7 w-7 grid place-items-center rounded-lg text-findo-indigo hover:bg-findo-indigo/10 transition-colors">
                    <Printer size={15} />
                  </button>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{g.list.length}</span>
                </div>
              </div>
              <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
                {g.list.map((it) => {
                  const Icon = IconFor(it.category);
                  return (
                    <button key={it.id} onClick={() => navigate(`/b2b/registry?item=${it.id}`)}
                      data-testid={`shelf-item-${it.id}`}
                      className="w-full text-left flex items-center gap-3 rounded-xl border border-border p-2 hover:border-findo-indigo hover:bg-slate-50 transition-colors">
                      {it.image_base64 ? (
                        <img src={it.image_base64} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <span className="h-10 w-10 grid place-items-center rounded-lg bg-slate-100 text-slate-400 shrink-0"><Icon size={16} /></span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.description || labelFor(it.category)}</p>
                        <p className="text-xs text-slate-400">{labelFor(it.category)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
}

function KpiCard({ icon: Icon, tone, label, value }) {
  const tones = {
    indigo: "text-findo-indigo bg-findo-indigo/10",
    teal: "text-teal-600 bg-teal-100",
    amber: "text-amber-600 bg-amber-100",
  };
  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-4 flex items-center gap-3">
      <span className={`h-11 w-11 grid place-items-center rounded-xl ${tones[tone]}`}><Icon size={20} /></span>
      <div>
        <p className="text-2xl font-display font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

function ScanDialog({ open, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [camState, setCamState] = useState("idle"); // idle | starting | running | error
  const scannerRef = useRef(null);
  const REGION_ID = "storage-qr-reader";

  const resolve = useCallback(async (raw) => {
    const id = parseItemId(raw);
    if (!id) { toast.error(t("stg.codeUnreadable")); return; }
    setBusy(true);
    try {
      await api(`/items/${id}`);
      toast.success(t("stg.itemFound"));
      onClose();
      navigate(`/b2b/registry?item=${id}`);
    } catch (e) {
      toast.error(t("stg.noItemMatch"));
    } finally { setBusy(false); }
  }, [navigate, onClose, t]);

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    let scanner = null;
    setCamState("starting");
    (async () => {
      try {
        scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (stopped) return;
            stopped = true;
            scanner.stop().catch(() => {}).finally(() => resolve(decoded));
          },
          () => {},
        );
        if (!stopped) setCamState("running");
      } catch (e) {
        setCamState("error");
      }
    })();
    return () => {
      stopped = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch { /* ignore */ } });
        scannerRef.current = null;
      }
    };
  }, [open, resolve]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><QrCode size={20} className="text-findo-indigo" /> {t("stg.scanTitle")}</DialogTitle>
          <DialogDescription>{t("stg.scanDesc")}</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl overflow-hidden bg-slate-900 relative" style={{ minHeight: 240 }}>
          <div id={REGION_ID} className="w-full" data-testid="scan-camera-region" />
          {camState !== "running" && (
            <div className="absolute inset-0 grid place-items-center text-center p-4 text-slate-300">
              {camState === "starting" && <p className="text-sm flex items-center gap-2"><Camera size={16} /> {t("stg.camStarting")}</p>}
              {camState === "error" && (
                <div data-testid="scan-camera-error">
                  <Camera size={26} className="mx-auto mb-2 opacity-60" />
                  <p className="text-sm">{t("stg.camUnavailable")}</p>
                  <p className="text-xs text-slate-400 mt-1">{t("stg.camHint")}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pt-2">
          <label className="text-sm font-semibold text-slate-700">{t("stg.manualLabel")}</label>
          <form className="flex gap-2 mt-1.5" onSubmit={(e) => { e.preventDefault(); if (manual.trim()) resolve(manual.trim()); }}>
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder={t("stg.manualPh")} className="h-10 rounded-xl" data-testid="scan-manual-input" />
            <Button type="submit" disabled={busy || !manual.trim()} className="rounded-xl" data-testid="scan-manual-submit">{t("stg.search")}</Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
